import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import DashboardShell from '../components/ui/DashboardShell';
import Input from '../components/ui/Input';
import LanguageSwitcher from '../components/ui/LanguageSwitcher';
import ThemeToggle from '../components/ui/ThemeToggle';

const ProfileSettingsPage = () => {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const { showToast } = useToast();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    setFirstName(user?.firstName || '');
    setLastName(user?.lastName || '');
    setPhone(user?.phone || '');
    setAvatarPreview(user?.profilePicture || null);
  }, [user]);

  const onAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('firstName', firstName);
      formData.append('lastName', lastName);
      formData.append('phone', phone);
      if (avatarFile) formData.append('file', avatarFile);

      await api.put('/auth/profile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refreshUser();
      setAvatarFile(null);
      showToast(t('profile.profileUpdated'), 'success');
    } catch (err) {
      showToast(err.response?.data?.message || t('profile.profileUpdateFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      showToast(t('profile.passwordMismatch'), 'error');
      return;
    }
    if (newPassword.length < 8) {
      showToast(t('profile.passwordMinLength'), 'error');
      return;
    }
    setChangingPassword(true);
    try {
      await api.put('/auth/change-password', { currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showToast(t('profile.passwordChanged'), 'success');
    } catch (err) {
      showToast(err.response?.data?.message || t('profile.passwordChangeFailed'), 'error');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <DashboardShell title={t('profile.title')} subtitle={t('profile.subtitle')}>
      <form onSubmit={saveProfile} className="max-w-xl space-y-6">
        <div className="flex items-center gap-6">
          <div className="h-20 w-20 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
            {avatarPreview ? (
              <img src={avatarPreview} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-2xl text-zinc-600 dark:text-zinc-500">
                {firstName?.[0]}
              </div>
            )}
          </div>
          <label className="cursor-pointer rounded-lg border border-zinc-300 dark:border-zinc-700 px-4 py-2 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800">
            {t('profile.uploadPhoto')}
            <input type="file" accept="image/*" className="hidden" onChange={onAvatarChange} />
          </label>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label={t('auth.firstName')} id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
          <Input label={t('auth.lastName')} id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
        </div>
        <Input label={t('auth.phone')} id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <Input label={t('auth.email')} id="email" value={user?.email || ''} disabled className="opacity-60" />

        <button type="submit" disabled={saving} className="rounded-lg bg-glow-600 px-6 py-2 text-sm font-medium text-white hover:bg-glow-500 disabled:opacity-50">
          {saving ? t('common.saving') : t('common.saveChanges')}
        </button>
      </form>

      <hr className="my-10 border-zinc-200 dark:border-zinc-800" />

      <div className="max-w-xl space-y-3">
        <h2 className="text-lg font-medium">{t('language.label')}</h2>
        <p className="text-sm text-zinc-600 dark:text-zinc-500">{t('language.hint')}</p>
        <LanguageSwitcher />
      </div>

      <hr className="my-10 border-zinc-200 dark:border-zinc-800" />

      <div className="max-w-xl space-y-3">
        <h2 className="text-lg font-medium">{t('profile.appearance')}</h2>
        <p className="text-sm text-zinc-600 dark:text-zinc-500">{t('profile.appearanceHint')}</p>
        <ThemeToggle />
      </div>

      <hr className="my-10 border-zinc-200 dark:border-zinc-800" />

      <form onSubmit={savePassword} className="max-w-xl space-y-4">
        <h2 className="text-lg font-medium">{t('profile.changePassword')}</h2>
        <Input label={t('profile.currentPassword')} id="currentPassword" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
        <Input label={t('profile.newPassword')} id="newPassword" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} />
        <Input label={t('profile.confirmNewPassword')} id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
        <button type="submit" disabled={changingPassword} className="rounded-lg border border-zinc-300 dark:border-zinc-700 px-6 py-2 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50">
          {changingPassword ? t('profile.updating') : t('profile.updatePassword')}
        </button>
      </form>
    </DashboardShell>
  );
};

export default ProfileSettingsPage;
